import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { EditablePrice } from "@/components/admin/EditablePrice";
import { EditableFeatureHighlights } from "@/components/admin/EditableFeatureHighlights";
import { SaleCountdown } from "@/components/product/SaleCountdown";
import { ExternalLink, Play, Infinity, Calendar, Heart } from "lucide-react";
import * as LucideIcons from 'lucide-react';
import { isSaleActive } from "@/lib/proPricing";
import type { Product } from "@/types/product";
import type { ProPricingResult } from "@/types/pricing";

interface ProductPurchaseCardProps {
  product: Product;
  isService: boolean;
  isCourse: boolean;
  hasAffiliatePlans: boolean;
  pro: ProPricingResult | null;
  isEditor: boolean;
  isEditMode: boolean;
  onBuyNow: () => void;
  onUpdateField: (field: string, value: any) => Promise<void>;
  onOpenBooking: (plan: string) => void;
  onOpenDemo: () => void;
}

export function ProductPurchaseCard({
  product,
  isService,
  isCourse,
  hasAffiliatePlans,
  pro,
  isEditor,
  isEditMode,
  onBuyNow,
  onUpdateField,
  onOpenBooking,
  onOpenDemo,
}: ProductPurchaseCardProps) {
  const saleActive = isSaleActive(product);

  return (
    <Card className="p-6 bg-card border-2 sticky top-32">
      <div className="space-y-6">
        {/* Product Title + Rating Header */}
        <div className="space-y-2">
          <h2 className="font-bold text-lg leading-tight">{product.title}</h2>
          <div className="flex items-center gap-2">
            {/* Blue Rating Circles */}
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <div
                  key={n}
                  className={`w-3.5 h-3.5 rounded-full ${
                    n <= Math.round(product.rating || 0)
                      ? 'bg-[hsl(201,73%,43%)]'
                      : 'bg-muted'
                  }`}
                />
              ))}
            </div>
            <a href="#reviews" className="text-sm text-muted-foreground hover:text-primary">
              {product.review_count || 0} reviews
            </a>
          </div>
          {product.subtitle && (
            <p className="text-sm text-muted-foreground italic">
              {product.subtitle.length > 60 ? product.subtitle.slice(0, 60) + '...' : product.subtitle}
            </p>
          )}
        </div>

        {/* Sale Countdown */}
        {saleActive && product.sale_ends_at && (
          <SaleCountdown endsAt={product.sale_ends_at} />
        )}

        {/* Price Section */}
        <div>
          {product.at_a_glance?.pricing_summary ? (
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase text-muted-foreground tracking-wider">
                Pricing
              </div>
              <p className="text-sm font-medium leading-relaxed">
                {product.at_a_glance.pricing_summary}
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {product.price && product.price > 0 && product.price !== product.msrp && product.msrp && (
                <Badge className="bg-red-500 text-white text-xs font-bold px-2 py-1">
                  -{Math.round((product.msrp - product.price) / product.msrp * 100)}%
                </Badge>
              )}
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold">
                  <EditablePrice value={product.price} onSave={value => onUpdateField('price', value)} />
                </span>
                {product.price && product.price > 0 && product.price !== product.msrp && product.msrp && (
                  <span className="text-lg text-muted-foreground line-through">
                    <EditablePrice value={product.msrp} onSave={value => onUpdateField('msrp', value)} />
                  </span>
                )}
              </div>
              {product.price && product.price > 0 && (
                <span className="text-sm text-muted-foreground">
                  /
                  {isEditor && isEditMode ? (
                    <select
                      value={product.price_duration || 'mo'}
                      onChange={e => onUpdateField('price_duration', e.target.value)}
                      className="inline bg-transparent border-b border-dashed border-orange-400 text-foreground px-1 cursor-pointer hover:border-orange-600"
                    >
                      <option value="mo">mo</option>
                      <option value="yr">yr</option>
                      <option value="one-time">one-time</option>
                      <option value="user">user</option>
                      <option value="seat">seat</option>
                      <option value="custom">custom</option>
                    </select>
                  ) : (
                    product.price_duration || 'mo'
                  )}
                </span>
              )}
            </div>
          )}
        </div>

        {/* CTA Buttons */}
        {product.price_duration === 'custom' ? (
          /* Custom Pricing CTA */
          <div className="space-y-2">
            <Button 
              className="w-full h-12 text-base font-bold bg-yellow-400 hover:bg-yellow-500 text-gray-900"
              onClick={() => window.open(product.website_url, '_blank')}
            >
              <ExternalLink className="mr-2 h-5 w-5" />
              Visit Website for Pricing
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              Pricing varies by configuration
            </p>
          </div>
        ) : isCourse ? (
          /* Course CTAs */
          <div className="space-y-2">
            {product.access_url ? (
              <Button 
                className="w-full h-12 text-base font-bold bg-yellow-400 hover:bg-yellow-500 text-gray-900"
                onClick={() => window.open(product.access_url, '_blank')}
              >
                <ExternalLink className="mr-2 h-5 w-5" />
                Enroll Now
              </Button>
            ) : (
              <Button 
                className="w-full h-12 text-base font-bold bg-yellow-400 hover:bg-yellow-500 text-gray-900"
                onClick={onBuyNow}
              >
                View Course Details
              </Button>
            )}
            
            {product.demo_video_url && (
              <Button 
                variant="outline"
                className="w-full h-12 text-base font-bold"
                onClick={onOpenDemo}
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Preview
              </Button>
            )}
          </div>
        ) : isService && !hasAffiliatePlans ? (
          /* Service CTAs */
          <div className="space-y-2">
            <Button
              className="w-full h-12 text-base font-bold bg-yellow-400 hover:bg-green-500 text-gray-900"
              onClick={() => onOpenBooking('Discovery Call')}
            >
              Book a Call
            </Button>
            {product.demo_video_url && (
              <Button
                className="w-full h-12 text-base font-bold bg-white hover:bg-gray-100 text-gray-900 border border-gray-300"
                onClick={onOpenDemo}
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Demo
              </Button>
            )}
          </div>
        ) : (
          /* Product CTAs */
          <>
            {/* Plan Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium">Select a plan</span>
                <a href="#pricing" className="text-primary hover:underline">
                  View product details
                </a>
              </div>
              <Select defaultValue="tier1">
                <SelectTrigger className="w-full h-10 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {product.plans.map((plan: any, index: number) => (
                    <SelectItem key={index} value={`tier${index + 1}`}>
                      License Tier {index + 1}: ${plan.price}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Buy Button */}
            <Button
              className="w-full h-12 text-base font-bold bg-yellow-400 hover:bg-yellow-500 text-gray-900"
              onClick={onBuyNow}
            >
              {hasAffiliatePlans ? 'View Pricing' : 'Buy now'}
            </Button>
          </>
        )}

        {/* Guarantees */}
        <div className="space-y-2 pt-2 border-t">
          {isEditor && isEditMode && (
            <div className="space-y-2 mb-3">
              <p className="text-xs text-muted-foreground">
                Edit the features shown below (US-based support, Onboarding, etc.)
              </p>
              <EditableFeatureHighlights
                value={product.feature_highlights || []}
                onSave={value => onUpdateField('feature_highlights', value)}
              />
            </div>
          )}
          {!isEditMode && (product.feature_highlights && product.feature_highlights.length > 0 ? (
            product.feature_highlights.map((item: any, index: number) => {
              const IconComponent = item.type === 'lucide' ? (LucideIcons as any)[item.icon] : null;
              return (
                <div key={index} className="flex items-start gap-3 text-sm text-muted-foreground">
                  {item.type === 'lucide' && IconComponent ? (
                    <IconComponent className="h-5 w-5 shrink-0 mt-0.5" />
                  ) : (
                    <span className="text-xl shrink-0">{item.icon}</span>
                  )}
                  <span>{item.text}</span>
                </div>
              );
            })
          ) : (
            <>
              {isService ? (
                <>
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <span className="text-xl shrink-0">🇺🇸</span>
                    <span>US-based support team</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <span className="text-xl shrink-0">✅</span>
                    <span>Onboarding done-for-you</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <span className="text-xl shrink-0">⚡</span>
                    <span>Average first results in 7 days</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <Infinity className="h-5 w-5 shrink-0 mt-0.5" />
                    <span>Lifetime access</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <Calendar className="h-5 w-5 shrink-0 mt-0.5" />
                    <span>Refundable up to 60 days</span>
                  </div>
                </>
              )}
              <div className="flex items-start gap-3 text-sm text-muted-foreground">
                <Heart className="h-5 w-5 shrink-0 mt-0.5" />
                <div>
                  Covered by our{" "}
                  <a href="#guarantee" className="text-foreground hover:underline font-medium">
                    We Got Your Back guarantee
                  </a>
                </div>
              </div>
            </>
          ))}
        </div>

        {/* SELECT Badge */}
        {(product.is_select || isEditor && isEditMode) && (
          <div className="space-y-2">
            {isEditor && isEditMode && (
              <div className="flex items-center justify-between p-2 bg-muted rounded-lg">
                <label className="text-sm font-medium">Show SELECT badge</label>
                <Switch
                  checked={product.is_select || false}
                  onCheckedChange={checked => onUpdateField('is_select', checked)}
                />
              </div>
            )}
            
            {product.is_select && (
              <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-2">
                <div className="flex items-start gap-2">
                  <div className="bg-green-600 text-white text-xs font-bold px-2 py-1 rounded">
                    SELECT
                  </div>
                  <p className="text-xs text-muted-foreground leading-snug">
                    Thoroughly vetted by Circle Network for quality, performance, and support
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
